/**
 * The zone slider's DATA TIER: what `widget.binding` holds for a
 * `ZONE_SLIDER` node and how it is folded into the control's `[data]`.
 *
 * Two independent sources, both optional:
 *
 * - **Value / range / cost** — one dataset, one column + aggregation each
 *   (AVG of `list_price`, MIN of `floor_price`, …). Each mapped measure is
 *   registered with `/components/compute` as its own component when the Data
 *   tab's **Generate Configuration** runs (`DesignRuntimeService.
 *   connectZoneSlider`), exactly like a bound text box — so the number agrees
 *   with the charts and is never summarised in the browser.
 * - **Zones** — `zonesMode: 'rows'` turns every row of a (possibly different)
 *   dataset into one coloured band: label / from / to / colour columns. These
 *   ARE rows (a lookup table), so they come from `DesignDataService.loadRows`.
 *   With `zonesMode: 'static'` the inspector's zone list is used as-is.
 *
 * Anything not bound keeps its static (appearance) value, so a half-bound
 * slider still renders completely — and a bound one renders its static values
 * until the compute answer / rows arrive, instead of flashing empty.
 */

import type { WidgetBinding, WidgetContent } from '../design-builder.types';
import {
  ZONE_SLIDER_PALETTE,
  finiteOrNull,
  normaliseZones,
  type ZoneSliderZone,
} from '../../../global-components/zone-slider/zone-slider.utils';

type Row = Record<string, unknown>;

export type ZoneSliderZonesMode = 'static' | 'rows' | 'columns';

/**
 * The numbers the compute API can supply: value / min / max / cost, plus
 * `level0`, `level1`, … - where each "Levels from columns" level ends.
 */
export type ZoneSliderMeasureKey = 'value' | 'min' | 'max' | 'cost' | 'margin' | 'recordId' | `col:${string}` | `minAlt:${string}` | `maxAlt:${string}` | `level${number}` | `levelFrom${number}`;

export type ZoneSliderMeasures = Partial<Record<string, number>>;

/**
 * One block of the track ("Blocks from columns"): it runs FROM the value of
 * `fromField` TO the value of `field` (each summarised by its aggregation),
 * e.g. Floor -> Target. No From column = starts where the previous block ended
 * (the first at the Minimum); no To column = runs to the Maximum.
 */
export interface ZoneSliderLevel {
  label: string;
  color: string;
  /** The block's END ("To") column. */
  field: string;
  aggregation: string;
  /** The block's START ("From") column. */
  fromField: string;
  fromAggregation: string;
}

/** One mapped measure: what to ask the compute API for. */
export interface ZoneSliderMeasureBinding {
  key: ZoneSliderMeasureKey;
  dataset: string;
  field: string;
  aggregation: string;
}

export interface ZoneSliderBinding {
  dataset: string;
  valueField: string;
  valueAggregation: string;
  minField: string;
  minAggregation: string;
  maxField: string;
  maxAggregation: string;
  /** More columns compared with Minimum / Maximum (e.g. total_cost vs current_price). */
  minCompareFields: string[];
  maxCompareFields: string[];
  /** Which of the compared values is the track end. Unset = lowest (start) / highest (end). */
  minPick: 'lowest' | 'highest' | '';
  maxPick: 'lowest' | 'highest' | '';
  /** Signed % room past the picked start / end ("-10" = the handle can go 10% below
   *  the start, "10" = 10% above the end). Empty = the Design tab's extra room. */
  minAdjustPct: string;
  maxAdjustPct: string;
  costField: string;
  costAggregation: string;
  /** Margin % read straight from a column; wins over the cost-based margin. */
  marginField: string;
  marginAggregation: string;
  /** Margin % formula, e.g. `([Value] - [Cost]) / [Value] * 100`. */
  marginFormula: string;
  /** Dataset columns the margin formula names, e.g. `[total_cost]`; each is fetched as `col:<name>`. */
  marginColumns: string[];
  zonesMode: ZoneSliderZonesMode;
  /** Empty → same as `dataset`. */
  zonesDataset: string;
  zoneLabelField: string;
  zoneFromField: string;
  zoneToField: string;
  zoneColorField: string;
  /** The record the slider stands for (its uuid), e.g. for an approval. */
  recordIdField: string;
  /** zonesMode 'columns': the blocks, left to right. */
  levels: ZoneSliderLevel[];
}

export const EMPTY_ZONE_SLIDER_BINDING: ZoneSliderBinding = {
  dataset: '',
  valueField: '',
  valueAggregation: 'AVG',
  minField: '',
  minAggregation: 'MIN',
  maxField: '',
  maxAggregation: 'MAX',
  minCompareFields: [],
  maxCompareFields: [],
  minPick: '',
  maxPick: '',
  minAdjustPct: '',
  maxAdjustPct: '',
  costField: '',
  costAggregation: 'AVG',
  marginField: '',
  marginAggregation: 'AVG',
  marginFormula: '',
  marginColumns: [],
  zonesMode: 'static',
  zonesDataset: '',
  zoneLabelField: '',
  zoneFromField: '',
  zoneToField: '',
  zoneColorField: '',
  recordIdField: '',
  levels: [],
};

/** The stored binding (free-form record) as a fully defaulted object. */
export function zoneSliderBindingOf(raw: WidgetBinding | Record<string, unknown> | null | undefined): ZoneSliderBinding {
  const source = (raw ?? {}) as Record<string, unknown>;
  const out: ZoneSliderBinding = { ...EMPTY_ZONE_SLIDER_BINDING };
  (Object.keys(EMPTY_ZONE_SLIDER_BINDING) as Array<keyof ZoneSliderBinding>).forEach((key) => {
    const value = source[key];
    if (typeof value === 'string') (out as unknown as Record<string, string>)[key] = value;
  });
  const list = (value: unknown) => (Array.isArray(value) ? value.filter((col): col is string => typeof col === 'string' && !!col) : []);
  out.minCompareFields = list(source['minCompareFields']);
  out.maxCompareFields = list(source['maxCompareFields']);
  out.minPick = out.minPick === 'lowest' || out.minPick === 'highest' ? out.minPick : '';
  out.maxPick = out.maxPick === 'lowest' || out.maxPick === 'highest' ? out.maxPick : '';
  out.marginColumns = Array.isArray(source['marginColumns'])
    ? (source['marginColumns'] as unknown[]).filter((col): col is string => typeof col === 'string' && !!col)
    : [];
  out.zonesMode = out.zonesMode === 'rows' || out.zonesMode === 'columns' ? out.zonesMode : 'static';
  out.levels = Array.isArray(source['levels'])
    ? (source['levels'] as unknown[])
      .filter((level): level is Record<string, unknown> => !!level && typeof level === 'object')
      .map((level) => ({
        label: typeof level['label'] === 'string' ? level['label'] : '',
        color: typeof level['color'] === 'string' ? level['color'] : '',
        field: typeof level['field'] === 'string' ? level['field'] : '',
        aggregation: typeof level['aggregation'] === 'string' && level['aggregation'] ? level['aggregation'] : 'SUM',
        fromField: typeof level['fromField'] === 'string' ? level['fromField'] : '',
        fromAggregation: typeof level['fromAggregation'] === 'string' && level['fromAggregation'] ? level['fromAggregation'] : 'SUM',
      }))
    : [];
  return out;
}

/**
 * The control's STATIC `[data]`: identity + every appearance key as typed.
 *
 * Not `resolveWidgetData`: that overlay drops empty strings (a select's "use
 * the default"), which here would make a cleared prefix / suffix / badge text
 * spring back to its default. For this control an empty string is a value.
 */
export function zoneSliderStaticData(widget: WidgetContent): Record<string, unknown> {
  return { ...(widget.data ?? {}), ...(widget.appearance ?? {}) };
}

/** The binding as stored on the node: only the keys that carry a value. */
export function compactZoneSliderBinding(binding: ZoneSliderBinding): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  (Object.keys(binding) as Array<keyof ZoneSliderBinding>).forEach((key) => {
    const value = binding[key];
    if (key === 'levels' || key === 'marginColumns' || key === 'minCompareFields' || key === 'maxCompareFields') return;
    if (value && value !== EMPTY_ZONE_SLIDER_BINDING[key]) out[key] = value;
  });
  // A dataset with nothing mapped is still a chosen dataset.
  if (binding.dataset) out['dataset'] = binding.dataset;
  if (binding.zonesMode !== 'static') out['zonesMode'] = binding.zonesMode;
  if (binding.levels.length) out['levels'] = binding.levels.map((level) => ({ ...level }));
  if (binding.marginColumns.length) out['marginColumns'] = [...binding.marginColumns];
  if (binding.minCompareFields.length) out['minCompareFields'] = [...binding.minCompareFields];
  if (binding.maxCompareFields.length) out['maxCompareFields'] = [...binding.maxCompareFields];
  return out;
}

/** A typed number in a min / max list ("6500"), not a column name. */
function isNumberText(text: string): boolean {
  return /^-?\d+(\.\d+)?$/.test(String(text).trim());
}

/** The dataset the zone rows come from (falls back to the value dataset). */
export function zoneSliderZonesDataset(binding: ZoneSliderBinding): string {
  return binding.zonesMode === 'rows' ? binding.zonesDataset || binding.dataset : '';
}

/** The measures that have a dataset AND a column — what Generate registers. */
export function zoneSliderMeasureBindings(binding: ZoneSliderBinding): ZoneSliderMeasureBinding[] {
  if (!binding.dataset) return [];
  const pairs: Array<[ZoneSliderMeasureKey, string, string]> = [
    ['value', binding.valueField, binding.valueAggregation],
    ['min', binding.minField, binding.minAggregation],
    ['max', binding.maxField, binding.maxAggregation],
    ['cost', binding.costField, binding.costAggregation],
    ['margin', binding.marginField, binding.marginAggregation],
    // The handle's own column is read live from the handle, not fetched.
    ...binding.marginColumns
      .filter((col) => col !== binding.valueField)
      .map((col): [ZoneSliderMeasureKey, string, string] => [`col:${col}`, col, binding.valueAggregation]),
    // Columns compared for the track start / end: summarised like the handle, so they
    // are THIS record's values (the handle's summary), not the whole dataset's MIN / MAX.
    ...binding.minCompareFields.filter((col) => !isNumberText(col)).map((col): [ZoneSliderMeasureKey, string, string] => [`minAlt:${col}`, col, binding.valueAggregation]),
    ...binding.maxCompareFields.filter((col) => !isNumberText(col)).map((col): [ZoneSliderMeasureKey, string, string] => [`maxAlt:${col}`, col, binding.valueAggregation]),
  ];
  const levels = binding.zonesMode === 'columns'
    ? binding.levels.flatMap((level, index): Array<[ZoneSliderMeasureKey, string, string]> => [
      [`levelFrom${index}`, level.fromField, level.fromAggregation || 'SUM'],
      [`level${index}`, level.field, level.aggregation || 'SUM'],
    ])
    : [];
  return [...pairs, ...levels]
    .filter(([, field]) => !!field)
    .map(([key, field, aggregation]) => ({ key, dataset: binding.dataset, field, aggregation: aggregation || 'AVG' }));
}

/** Datasets whose ROWS this binding needs (the zone lookup table only). */
export function zoneSliderDatasets(binding: ZoneSliderBinding): string[] {
  const zones = zoneSliderZonesDataset(binding);
  return zones && binding.zoneToField ? [zones] : [];
}

/** True once anything is actually mapped — the widget counts as connected. */
export function zoneSliderIsBound(binding: ZoneSliderBinding): boolean {
  return zoneSliderMeasureBindings(binding).length > 0 || zoneSliderDatasets(binding).length > 0 || !!(binding.dataset && binding.recordIdField);
}

/**
 * The control's `[data]` with the bound parts overridden: `measures` are the
 * compute API's answers for value / min / max / cost (absent until Generate
 * has run), `rowsOf` returns a dataset's loaded rows for the zone table, or
 * null while they are not available yet.
 */
export function applyZoneSliderBinding(
  data: Record<string, unknown>,
  binding: ZoneSliderBinding,
  rowsOf: (dataset: string) => Row[] | null,
  measures: ZoneSliderMeasures | null | undefined = undefined,
): Record<string, unknown> {
  const out: Record<string, unknown> = { ...data };

  if (measures) {
    zoneSliderMeasureBindings(binding).forEach(({ key }) => {
      const computed = finiteOrNull(measures[key]);
      if (computed !== null) out[key] = computed;
    });
  }

  // "Levels from columns": each block ends at its column's value. Until every
  // mapped level has its number, the static zones keep showing (no flash).
  if (binding.zonesMode === 'columns' && binding.levels.length) {
    const zones = zonesFromLevels(binding.levels, measures);
    if (zones) {
      out['zones'] = zones;
      // No Minimum / Maximum column: the track spans the blocks themselves.
      const points = zones.flatMap((zone) => [zone.from, zone.to]).filter((point): point is number => typeof point === 'number' && Number.isFinite(point));
      if (points.length && !(binding.dataset && binding.minField)) out['min'] = Math.min(...points);
      if (points.length && !(binding.dataset && binding.maxField)) out['max'] = Math.max(...points);
    }
  }

  const zonesDataset = zoneSliderZonesDataset(binding);
  if (zonesDataset && binding.zoneToField) {
    const rows = rowsOf(zonesDataset);
    const zones = rows ? zonesFromRows(rows, binding) : [];
    if (zones.length) out['zones'] = zones;
  }

  // The track's first / last value (Minimum column, the first block's start /
  // last block's end, or the typed one) compared with more columns, e.g.
  // total_cost vs current_price: the lowest / highest (as configured) wins.
  // The Design tab's extra room (-10% / +10%) is then applied on top of it.
  const pickEnd = (key: 'min' | 'max', compare: string[], pick: 'lowest' | 'highest') => {
    if (!compare.length || !measures) return;
    // Only the picked columns decide (e.g. total_cost vs current_price).
    const candidates = compare
      .map((col) => ({ value: isNumberText(col) ? Number(col) : finiteOrNull(measures[`${key}Alt:${col}`]), from: col }))
      .filter((entry): entry is { value: number; from: string } => entry.value !== null);
    if (!candidates.length) return;
    const best = candidates.reduce((a, b) => ((pick === 'lowest' ? b.value < a.value : b.value > a.value) ? b : a));
    out[key] = best.value;
    out[`${key}From`] = best.from;
  };
  pickEnd('min', binding.minCompareFields, binding.minPick || 'lowest');
  pickEnd('max', binding.maxCompareFields, binding.maxPick || 'highest');
  // The handle's column (e.g. set_price) has no value ("-" / null, or no answer
  // at all): the handle sits on the start shown under the track, never on the
  // typed / default value (8,300).
  // A SUM over an empty set_price comes back as 0, not null: 0 counts as "no value"
  // too (a price is never 0) - else the handle sat on $0 and the track grew down to 0.
  const handle = finiteOrNull(measures?.['value']);
  if (binding.dataset && binding.valueField && (handle === null || handle === 0) && finiteOrNull(out['min']) !== null) {
    out['value'] = out['min'];
  }

  // "Minimum / Maximum adjustment (%)": the room past each picked end.
  const adjust = (text: string) => (text.trim() !== '' && Number.isFinite(Number(text)) ? Math.abs(Number(text)) : null);
  if (binding.minCompareFields.length && adjust(binding.minAdjustPct) !== null) out['minPaddingPct'] = adjust(binding.minAdjustPct);
  if (binding.maxCompareFields.length && adjust(binding.maxAdjustPct) !== null) out['maxPaddingPct'] = adjust(binding.maxAdjustPct);

  return out;
}

/**
 * The blocks of "Levels from columns", in ascending order of where they end
 * (data decides the order, not the list), colours filled from the palette.
 * Null while a mapped level has no computed value yet.
 */
export function zonesFromLevels(levels: ZoneSliderLevel[], measures: ZoneSliderMeasures | null | undefined): ZoneSliderZone[] | null {
  const zones: Array<{ label: string; color: string; from: number | null; to: number | null }> = [];
  for (let index = 0; index < levels.length; index++) {
    const level = levels[index];
    const from = level.fromField ? finiteOrNull(measures?.[`levelFrom${index}`]) : null;
    const to = level.field ? finiteOrNull(measures?.[`level${index}`]) : null;
    if ((level.fromField && from === null) || (level.field && to === null)) return null;
    // A block given its bounds the wrong way round (From above To) is still drawn.
    const [low, high] = from !== null && to !== null && from > to ? [to, from] : [from, to];
    zones.push({ label: level.label, color: level.color || ZONE_SLIDER_PALETTE[index % ZONE_SLIDER_PALETTE.length], from: low, to: high });
  }
  // Left to right by where each block starts (else ends); open ends sort last.
  zones.sort((a, b) => (a.from ?? a.to ?? Infinity) - (b.from ?? b.to ?? Infinity) || (a.to ?? Infinity) - (b.to ?? Infinity));
  return normaliseZones(zones);
}

/**
 * One band per row, in ascending order of its lower bound (then upper), with
 * rows that carry no usable bound dropped. Colours missing from the data are
 * filled from the palette by position, so an unstyled lookup table still
 * renders as distinct bands.
 */
export function zonesFromRows(rows: Row[], binding: ZoneSliderBinding): ZoneSliderZone[] {
  const zones = rows
    .map((row) => ({
      label: binding.zoneLabelField ? String(row?.[binding.zoneLabelField] ?? '') : '',
      color: binding.zoneColorField ? String(row?.[binding.zoneColorField] ?? '').trim() : '',
      from: binding.zoneFromField ? finiteOrNull(row?.[binding.zoneFromField]) : null,
      to: finiteOrNull(row?.[binding.zoneToField]),
    }))
    .filter((zone) => zone.from !== null || zone.to !== null)
    .sort((a, b) => (a.from ?? a.to ?? 0) - (b.from ?? b.to ?? 0) || (a.to ?? Infinity) - (b.to ?? Infinity));

  return normaliseZones(zones.map((zone, index) => ({
    ...zone,
    color: zone.color || ZONE_SLIDER_PALETTE[index % ZONE_SLIDER_PALETTE.length],
  })));
}
